import { Component, computed, OnDestroy, OnInit, signal, Signal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { UpdatePageTitle } from '../../../../../store/app/app.actions';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { StartPollingAccountingConnectionStatus } from '../../../../../store/accounting/accounting.actions';
import { AccountingService } from '../../_services/accounting/accounting.service';
import { AccountingConnectionStatus, AccountingProviderCompany } from '../../../../../_interfaces/accounting.interface';
import { AccountingState } from '../../../../../store/accounting/accounting.state';
import { Subscription } from 'rxjs';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-acc-connection',
  imports: [LoaderComponent, RouterLink, NgTemplateOutlet],
  templateUrl: './account-connection.component.html',
})
export class AccountConnectionComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly toastService = inject(ToastService);
  readonly accountingService = inject(AccountingService);
  readonly accountingState = inject(AccountingState);

  protected accountingProvider: Signal<AccountingProviderCompany | undefined> = computed(() =>
    this.accountingService.provider(),
  );

  readonly subscriptions = new Subscription();
  protected queryParams = new HttpParams();
  protected isLoadingConnection = signal(true);

  protected statusSuccess: Signal<boolean> = computed(() => {
    return this.route.snapshot.queryParams['status'] === 'success';
  });

  protected statusError: Signal<boolean> = computed(() => {
    return this.route.snapshot.queryParams['status'] === 'error';
  });

  protected requestCancelled: Signal<boolean> = computed(() => {
    return this.route.snapshot.queryParams['error'] === 'access_denied';
  });

  protected accountingConnectionComplete: Signal<boolean> = computed(
    () => this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete,
  );
  protected accountingConnectionHasTenants: Signal<boolean> = computed(
    () => this.accountingService.connectionStatus() === AccountingConnectionStatus.HasTenants,
  );
  protected accountingConnectionError: Signal<boolean> = computed(() => {
    return (
      this.accountingService.connectionStatus() === AccountingConnectionStatus.Error ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.HttpError ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.AuthError
    );
  });

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Integrations'));
  }

  ngOnInit(): void {
    if (this.statusSuccess()) {
      // Successful authorisation from provider, now to check if the account setup has been successful by polling for status updates
      this.store.dispatch(new StartPollingAccountingConnectionStatus());
      this.subscriptions.add(
        // Polling does not stop on "HasToken" or "Pending" status, so can depend on remaining statuses:
        this.accountingState.stopPolling$.subscribe({
          next: (data) => {
            if (data) {
              this.isLoadingConnection.set(false);
              // Check for errors first
              if (this.accountingConnectionError()) {
                // display error in page
                return;
              }

              // Check if it's a new connection (bank accounts have been created)
              // multiple tenants detected, redirect to select an organisation
              if (this.accountingConnectionHasTenants()) {
                this.router.navigate(['../select-organisation'], { relativeTo: this.route }).then(() => {});
              }

              if (this.accountingConnectionComplete()) {
                // always select a source-account, this gets reset even on a reconnection.
                this.router.navigate(['../select-source-account'], { relativeTo: this.route }).then(() => {});
              }
            }
          },
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
